import { Component, HostListener, OnDestroy, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SsoAccountData } from '../../../sso-account/domain/models/sso-account.model';
import { AdvisorData } from '../../../../../lib/advisor/advisor.model';
import { IdVerseStatusType } from './components/id-verse-status-message/id-verse-status-message.model';
import { IdVerseFailedAction } from '../../domain/models/identity-verification.model';
import { IdVerseContentConfig, IdVerseFlowType, IdVerseViewData } from './local-domain/models/id-verse-content.model';
import { IdVerseContentFacade } from './local-domain/+state/id-verse-content.facade';
import { IdVerseDesktopFlowService } from './local-domain/+state/id-verse-desktop-flow.service';
import { IdVerseFlowHandlerService } from './local-domain/+state/id-verse-flow-handler.service';
import { IdVerseMobileFlowService } from './local-domain/+state/id-verse-mobile-flow.service';
import { IdVerseContentStateService } from './local-domain/+state/id-verse-content-state.service';

@UntilDestroy()
@Component({
    selector: 'ifc-id-verse-content',
    templateUrl: './id-verse-content.component.html',
    styleUrls: ['./id-verse-content.component.scss'],
    providers: [IdVerseContentFacade, IdVerseFlowHandlerService, IdVerseContentStateService, IdVerseDesktopFlowService, IdVerseMobileFlowService],
    standalone: false
})
export class IdVerseContentComponent implements OnInit, OnDestroy {
  @Input() advisorData: AdvisorData;
  @Input() loanId?: string;
  @Input() config?: IdVerseContentConfig;
  @Output() verified: EventEmitter<SsoAccountData> = new EventEmitter<SsoAccountData>();
  @Output() failedNoRetry: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly IdVerseFlowType = IdVerseFlowType;
  readonly IdVerseStatusType = IdVerseStatusType;
  readonly IdVerseFailedAction = IdVerseFailedAction;

  viewData: IdVerseViewData;

  constructor(private readonly facade: IdVerseContentFacade) { }

  ngOnInit(): void {
    this.facade.init(this.config);
    this.facade.getComponentData$()
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.viewData = data;
        this.handleFailedNoRetry(data);
      });
  }

  onClickSuccessContinue(): void {
    this.verified.emit(this.viewData.currentSsoAccount);
  }

  onClickPendingContinue(): void {
    this.facade.onClickPendingContinue(this.viewData, this.config);
  }

  onClickTryAgain(): void {
    this.facade.onClickTryAgain(this.viewData);
  }

  onClickSwitchToKba(): void {
    this.facade.onClickSwitchToKba(this.loanId);
  }

  onClickContinueInBrowser(): void {
    this.facade.onClickContinueInBrowser();
  }

  @HostListener('window:pageshow')
  onPageShowAfterExternalReturn(): void {
    this.facade.restoreFlowAfterExternalReturn(this.config);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChangeAfterExternalReturn(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }

    this.facade.restoreFlowAfterExternalReturn(this.config);
  }

  private handleFailedNoRetry(data: IdVerseViewData): void {
    if (data.state.currentFlowType !== IdVerseFlowType.Failed) {
      return;
    }
    const action = data.state.failedAction;
    if (action === IdVerseFailedAction.ShowError || action === IdVerseFailedAction.ShowErrorOnExpired) {
      this.failedNoRetry.emit(action === IdVerseFailedAction.ShowErrorOnExpired);
    }
  }

  ngOnDestroy(): void {
    this.facade.clear();
  }
}
